/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Sign-in slice assembly.
 *
 * The feature is split by authentication flow — see `thunks/`, `reducers/`
 * and `selectors/`, with everything more than one flow needs in `common/`.
 * This module only wires those parts into a single slice and re-exports the
 * feature's public surface, so importing `signInSlice` still gives callers
 * every thunk, action and selector it used to.
 */

import { createSlice } from "@reduxjs/toolkit";

import { INITIAL_STATE } from "./signIn.state";
import {
  dbsLicensePlateReducers,
  focusNavigationReducers,
  ipAutoLoginReducers,
  passwordResetReducers,
  registerAuthConfigReducers,
  registerCredentialsSignInReducers,
  registerDbsLicensePlateReducers,
  registerIpAutoLoginReducers,
  registerPasswordResetReducers,
  registerQrCodeReducers,
  registerTokenReducers,
  signInFormReducers
} from "./reducers";

const signInSlice = createSlice({
  name: "signIn",
  initialState: INITIAL_STATE,
  reducers: {
    ...signInFormReducers,
    ...focusNavigationReducers,
    ...passwordResetReducers,
    ...dbsLicensePlateReducers,
    ...ipAutoLoginReducers
  },
  extraReducers: (builder) => {
    registerAuthConfigReducers(builder);
    registerIpAutoLoginReducers(builder);
    registerCredentialsSignInReducers(builder);
    registerTokenReducers(builder);
    registerQrCodeReducers(builder);
    registerPasswordResetReducers(builder);
    registerDbsLicensePlateReducers(builder);
  }
});

export const {
  setActiveTab,
  setUserId,
  setPassword,
  setRecoveryEmail,
  togglePasswordVisibility,
  setAuthCode,
  setQrCodeUrl,
  resetAuthOrchestration,
  resetSignInState,
  clearSignInError,
  resetPasswordFlowState,
  setMobileDialogDismissed,
  setFocusedField,
  focusNext,
  focusPrev,
  switchTabLeft,
  switchTabRight,
  cancelDbsPolling,
  resetDbsState
} = signInSlice.actions;

// Orchestration actions the thunks dispatch. They are declared outside the
// slice to keep the flow modules free of a dependency back on it — see
// signIn.actions.ts — and are re-exported here so callers see one surface.
export {
  markAuthTypeAttempted,
  setAuthOrchestrationStatus,
  setIpAutoLoginStatus,
  sessionInvalidated
} from "./signIn.actions";

export {
  initializeSignIn,
  signInWithCredentials,
  resolveNextAuthStep,
  startIpAutoLoginSignIn,
  startDbsLicensePlateFlow,
  startDbsPolling,
  completeDbsBinding,
  sendForgotPasswordLink,
  validateResetEmailToken,
  submitUpdatedPassword,
  rehydrateTokenFromStorage,
  generateQrCodeBitmap
} from "./thunks";

export * from "./selectors";

export default signInSlice.reducer;
